import { useClock, useDataStatus } from '../lib/data'
import { remoteConfig } from '../lib/auth/supabase'
import { countPhrase, dayClockPhrase } from '../components/connections/labels'
import { PanelSkeleton, PanelUnavailable, Section } from '../components/connections/parts'
import { ApiPath, FactCard, FactRow, NoteRow, SettingsScreen } from '../components/settings/parts'

// ============================================================================
// PRIVACY & DATA — the machine-readability rule, made visible.
//
// NO MOCKUP SOURCE. What this screen is FOR: CLAUDE.md's product identity says
// "every datum readable by human AND machine — no feature ships API-less", and
// that promise is invisible everywhere else in the app. Here it is the subject:
// where the bytes are, what the API index is, how much of it is searchable, and
// the rule that anything a model wrote carries a label saying so.
//
// TWO THINGS IT DELIBERATELY DOES NOT HAVE. There is no Export button and no
// Delete-everything button, because neither endpoint exists — and a button that
// silently does nothing on THIS screen, of all screens, would be the loudest
// possible contradiction of what it is claiming. They are stated as absent, in
// words, with the thing that stands in for them today (the API index) named.
//
// WHERE THE STORAGE LIST COMES FROM — apps/worker/wrangler.toml, not memory:
// the `[[r2_buckets]]` binding (lifeos-media), the `[[vectorize]]` index
// (lifeos-search), and the Supabase secrets block. CLAUDE.md → Architecture
// carries the same three.
// ============================================================================

export function PrivacyData() {
  const status = useDataStatus()
  const { data: clock } = useClock()
  const config = remoteConfig()

  return (
    <SettingsScreen
      active="privacy"
      title="Privacy & data"
      subtitle="One owner · one workspace · nothing shared with anyone"
    >
      <Section label="The API">
        {config ? (
          <>
            <div className="flex flex-wrap items-center gap-[10px]">
              <DocLink href={`${config.apiBaseUrl}/api/docs`} label="Open the API index" />
              <DocLink href={`${config.apiBaseUrl}/api/openapi.json`} label="openapi.json" />
            </div>
            <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
              Every route in LifeOS is declared with its own schema, so the spec at{' '}
              <ApiPath>/api/openapi.json</ApiPath> is generated from the code rather than written
              beside it. A route that is not in the spec does not exist. Both links need your
              bearer token to return data — opening them in a browser tab shows the shapes, not
              your entries.
            </p>
          </>
        ) : (
          <NoteRow>
            This build has no Worker configured, so there is no API index to link to. The demo
            dataset lives in the browser&rsquo;s own memory and there is nothing on the other end
            of a request. A configured build serves the index at <ApiPath>/api/docs</ApiPath> and
            the spec at <ApiPath>/api/openapi.json</ApiPath>.
          </NoteRow>
        )}
      </Section>

      <Section label="What is stored where">
        <FactCard>
          <FactRow
            label="Supabase Postgres"
            value="Everything you write and everything a connector pulls: journal entries, tasks, courses, assignments, saves, transcripts, chat threads. Every table carries your user id and an owner-only RLS policy. The browser never queries it directly — reads go through the Worker."
          />
          <FactRow
            label="Cloudflare R2"
            value="Lecture and voice-memo audio, in transit only. A file is uploaded, the desktop worker transcribes it, and the audio is deleted from R2 — the raw recording is archived on your own PC disk, not in the cloud."
          />
          <FactRow
            label="Cloudflare Vectorize"
            value="One embedding per indexed item, so search can find meaning and not only words. A vector's id is the Postgres row id: no text lives here, only the numbers and the pointer back."
          />
          <FactRow
            label="This browser"
            value="Your theme, your UI size, and the two journal-composer preferences. Kept in localStorage, never sent anywhere, and not cleared by signing out."
          />
        </FactCard>
      </Section>

      <Section label="How much of it is searchable">
        {status.isPending && <PanelSkeleton height={96} />}
        {status.error && <PanelUnavailable error={status.error} />}
        {status.data && (
          <>
            <FactCard>
              <FactRow label="Indexed" value={countPhrase(status.data.index.itemsIndexed)} />
              <FactRow
                label="Waiting"
                value={
                  status.data.index.pending === 0
                    ? 'nothing'
                    : `${countPhrase(status.data.index.pending)} transcripts`
                }
              />
              <FactRow
                label="Confirmed current"
                value={
                  status.data.index.currentAsOf && clock
                    ? dayClockPhrase(status.data.index.currentAsOf, clock.date)
                    : null
                }
                missingReason="Nothing has been embedded with a recorded timestamp yet, so there is no date to show."
              />
              {status.data.index.blockedBy && (
                <FactRow label="Blocked by" value={status.data.index.blockedBy} />
              )}
            </FactCard>
            <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
              &ldquo;Indexed&rdquo; undercounts on purpose. Journal entries, tasks and saves are
              embedded fire-and-forget with no stored per-row outcome, so they are counted as done;
              transcripts are the one table that records success and failure, so they are the only
              real source of a waiting count. Understating search coverage is the honest side to be
              wrong on.
            </p>
          </>
        )}
      </Section>

      <Section label="Anything a model wrote says so">
        <NoteRow>
          Summaries, extracted syllabus facts, study material and chat answers all carry their
          provenance where they are shown — which model, and what it read. The app never presents a
          generated sentence as something you wrote or something a source stated. If a label is
          missing on something that looks machine-made, that is a bug and not a style choice.
        </NoteRow>
      </Section>

      <Section label="Export and delete">
        <NoteRow>
          Not built yet — and there is no button here pretending otherwise. What exists today is
          the API: every entity has a documented GET, so the whole archive is readable with your
          token and a script, which is the machine-readability rule doing the job an export button
          would do. Deleting everything is a Supabase operation on your own project, deliberately
          not a control this app exposes: a single-user app with one irreversible red button is a
          bad trade.
        </NoteRow>
      </Section>
    </SettingsScreen>
  )
}

/**
 * A link OUT of the app, styled like the outline action button — and it is an
 * anchor rather than a button because it navigates, and because the owner should
 * be able to see the URL before he follows it.
 */
function DocLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={href}
      className="mo-press flex flex-none items-center gap-[7px] whitespace-nowrap rounded-button border border-line bg-surface px-3 py-[6px] text-[11.5px] font-medium leading-none text-ink-2 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2"
    >
      {label}
    </a>
  )
}
